import React, { useEffect, useState } from 'react';
import { Trash2Icon, Maximize2Icon, CropIcon, AlertTriangleIcon } from 'lucide-react';
interface Design {
  id: string;
  image: string;
  x: number;
  y: number;
  scale: number;
  zIndex: number;
}
interface DesignControlsProps {
  designs: Design[];
  selectedDesignId: string | null;
  selectedDesign: Design | undefined;
  onDesignSelect: (id: string) => void;
  onScaleChange: (scale: number) => void;
  onRemoveDesign: (id: string) => void;
  onRemoveBackground: (id: string) => void;
  onUpscaleImage: (id: string) => void;
  onAutoTrim: (id: string) => void;
}
export function DesignControls({
  designs,
  selectedDesignId,
  selectedDesign,
  onDesignSelect,
  onScaleChange,
  onRemoveDesign,
  onRemoveBackground,
  onUpscaleImage,
  onAutoTrim
}: DesignControlsProps) {
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [showWarningTooltip, setShowWarningTooltip] = useState(false);
  useEffect(() => {
    if (selectedDesign) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({
          width: img.width,
          height: img.height
        });
      };
      img.src = selectedDesign.image;
    } else {
      setImageDimensions(null);
    }
  }, [selectedDesign]);
  if (designs.length === 0) {
    return null;
  }
  const printWidth = imageDimensions ? (imageDimensions.width / 300).toFixed(2) : '0';
  const printHeight = imageDimensions ? (imageDimensions.height / 300).toFixed(2) : '0';
  const isTooSmall = imageDimensions && imageDimensions.width / 300 < 3;
  return <div className="space-y-4" data-id="element-265">
      <div data-id="element-266">
        <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-267">
          Design Layers
        </h3>
        <div className="space-y-2" data-id="element-268">
          {designs.map((design, index) => <div key={design.id} onClick={() => onDesignSelect(design.id)} className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${selectedDesignId === design.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-269">
              <div className="flex items-center justify-between" data-id="element-270">
                <div className="flex items-center gap-3" data-id="element-271">
                  <img src={design.image} alt={`Design ${index + 1}`} className="w-12 h-12 object-contain rounded bg-gray-50" data-id="element-272" />
                  <span className="text-sm font-medium text-gray-700" data-id="element-273">
                    Layer {index + 1}
                  </span>
                </div>
                <button onClick={e => {
              e.stopPropagation();
              onRemoveDesign(design.id);
            }} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors" data-id="element-274">
                  <Trash2Icon className="w-4 h-4" data-id="element-275" />
                </button>
              </div>
            </div>)}
        </div>
      </div>

      {selectedDesign && <div className="pt-4 border-t border-gray-200" data-id="element-276">
          <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-277">
            Layer Controls
          </h3>

          {/* Art Specifications */}
          {imageDimensions && <div className={`mb-4 p-3 border rounded-lg relative ${isTooSmall ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-200'}`} data-id="element-278">
              <div className="flex items-start justify-between" data-id="element-279">
                <h4 className={`text-xs font-semibold mb-2 ${isTooSmall ? 'text-red-900' : 'text-blue-900'}`} data-id="element-280">
                  📏 Art Specifications
                </h4>

                {isTooSmall && <div className="relative" data-id="element-281">
                    <button onMouseEnter={() => setShowWarningTooltip(true)} onMouseLeave={() => setShowWarningTooltip(false)} onClick={() => setShowWarningTooltip(!showWarningTooltip)} className="flex items-center gap-1 text-red-700 hover:text-red-900 transition-colors" data-id="element-282">
                      <AlertTriangleIcon className="w-4 h-4" data-id="element-283" />
                      <span className="text-xs font-semibold" data-id="element-284">WARNING</span>
                    </button>

                    {showWarningTooltip && <div className="absolute right-0 top-6 w-64 p-3 bg-white border-2 border-red-500 rounded-lg shadow-lg z-50" data-id="element-285">
                        <div className="text-xs text-gray-900 space-y-2" data-id="element-286">
                          <p className="font-semibold text-red-700" data-id="element-287">
                            ⚠️ Art Resolution Too Low
                          </p>
                          <p data-id="element-288">
                            Your art resolution is way too low to print. Average
                            art is <strong data-id="element-289">3 inches to 12 inches</strong> at 300
                            DPI.
                          </p>
                          <p className="text-gray-600" data-id="element-290">
                            You can continue to proof, however we may need to
                            charge art fees to make the art usable in your
                            design.
                          </p>
                        </div>
                        <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-l-2 border-t-2 border-red-500 transform rotate-45" data-id="element-291"></div>
                      </div>}
                  </div>}
              </div>

              <div className={`space-y-1 text-xs ${isTooSmall ? 'text-red-800' : 'text-blue-800'}`} data-id="element-292">
                <div className="flex justify-between" data-id="element-293">
                  <span className="font-medium" data-id="element-294">Resolution:</span>
                  <span className="font-mono" data-id="element-295">
                    {imageDimensions.width} × {imageDimensions.height} px
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-296">
                  <span className="font-medium" data-id="element-297">Print Size @ 300 DPI:</span>
                  <span className="font-mono" data-id="element-298">
                    {printWidth}" × {printHeight}"
                  </span>
                </div>
              </div>
            </div>}

          <div className="space-y-3" data-id="element-299">
            <div data-id="element-300">
              <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-301">
                Size: {Math.round(selectedDesign.scale * 100)}%
              </label>
              <input type="range" min="0.1" max="2" step="0.05" value={selectedDesign.scale} onChange={e => onScaleChange(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500" data-id="element-302" />
            </div>

            <div className="space-y-2" data-id="element-303">
              <button onClick={() => onRemoveBackground(selectedDesign.id)} className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-304">
                Remove Background
              </button>

              <button onClick={() => onUpscaleImage(selectedDesign.id)} className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-305">
                <Maximize2Icon className="w-4 h-4" data-id="element-306" />
                Upscale Image (4K)
              </button>

              <button onClick={() => onAutoTrim(selectedDesign.id)} className="w-full px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-307">
                <CropIcon className="w-4 h-4" data-id="element-308" />
                Auto Trim Dead Space
              </button>
            </div>
          </div>
        </div>}
    </div>;
}